import { NavLink, useNavigate } from "react-router-dom";
import {
  Home, Inbox, Users, Building2, Briefcase, FileSignature, BarChart3, Settings,
  Sparkles, CheckSquare, Activity, Megaphone, ClipboardList, FileText,
  Workflow, ListChecks,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useChordShortcuts } from "@/hooks/useKeyboardShortcuts";

const NAV = [
  { to: "/", label: "Home", icon: Home, chord: "h" },
  { to: "/inbox", label: "Inbox", icon: Inbox, chord: "i" },
  { to: "/contacts", label: "Contacts", icon: Users, chord: "c" },
  { to: "/companies", label: "Companies", icon: Building2, chord: "o" },
  { to: "/deals", label: "Deals", icon: Briefcase, chord: "d" },
  { to: "/contracts", label: "Contracts", icon: FileSignature, chord: "t" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, chord: "k" },
  { to: "/approvals", label: "Approvals", icon: Sparkles, chord: "a" },
  { to: "/reports", label: "Reports", icon: BarChart3, chord: "r" },
] as const;

const LOOPS_NAV = [
  { to: "/workflows", label: "Workflows", icon: Workflow },
  { to: "/lists", label: "Lists", icon: ListChecks },
] as const;

const PORTAL_NAV = [
  { to: "/projects", label: "Projects", icon: Briefcase },
] as const;

const CAMPAIGN_NAV = [
  { to: "/sequences", label: "Sequences", icon: Activity },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/templates", label: "Templates", icon: FileText },
  { to: "/forms", label: "Forms", icon: ClipboardList },
] as const;

export default function Sidebar() {
  const navigate = useNavigate();
  useChordShortcuts(
    NAV.map((n) => ({
      keys: ["g", n.chord] as [string, string],
      handler: () => navigate(n.to),
      description: `Go to ${n.label}`,
    })),
  );

  return (
    <aside className="flex h-full w-[232px] shrink-0 flex-col bg-side text-ink-side">
      <div className="flex h-[52px] items-center gap-2 border-b border-line-side px-4">
        <div className="grid h-7 w-7 place-items-center rounded bg-gradient-to-br from-brand to-brand-bright text-[11px] font-bold text-brand-ink">
          LG
        </div>
        <div className="flex flex-col leading-none">
          <span className="font-display text-[13px] font-semibold tracking-tight text-white">
            LuzGrid CRM
          </span>
          <span className="text-[10px] uppercase tracking-wider text-ink-side-dim">
            React Preview
          </span>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 scrollbar-thin">
        <ul className="space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, chord }) => (
            <NavItem key={to} to={to} label={label} Icon={Icon} chord={`g ${chord}`} end={to === "/"} />
          ))}
        </ul>

        <div className="mt-6 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-ink-side-dim">
          Loops
        </div>
        <ul className="mt-1 space-y-0.5">
          {LOOPS_NAV.map(({ to, label, icon: Icon }) => (
            <NavItem key={to} to={to} label={label} Icon={Icon} />
          ))}
        </ul>

        <div className="mt-6 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-ink-side-dim">
          Client portal
        </div>
        <ul className="mt-1 space-y-0.5">
          {PORTAL_NAV.map(({ to, label, icon: Icon }) => (
            <NavItem key={to} to={to} label={label} Icon={Icon} />
          ))}
        </ul>

        <div className="mt-6 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-ink-side-dim">
          Marketing
        </div>
        <ul className="mt-1 space-y-0.5">
          {CAMPAIGN_NAV.map(({ to, label, icon: Icon }) => (
            <NavItem key={to} to={to} label={label} Icon={Icon} />
          ))}
        </ul>

        <div className="mt-6 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-ink-side-dim">
          Workspace
        </div>
        <ul className="mt-1 space-y-0.5">
          <NavItem to="/admin/users" label="Admin" Icon={Settings} />
        </ul>
      </nav>

    </aside>
  );
}

function NavItem({
  to, label, Icon, chord, end,
}: { to: string; label: string; Icon: typeof Inbox; chord?: string; end?: boolean }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          cn(
            "group flex h-8 items-center gap-2.5 rounded px-2.5 text-[13px] font-medium transition-colors",
            isActive
              ? "bg-side-active text-white"
              : "text-ink-side hover:bg-side-hover hover:text-white",
          )
        }
      >
        <Icon className="h-4 w-4 opacity-80" />
        <span className="flex-1">{label}</span>
        {chord && (
          <kbd className="hidden font-mono text-[9px] text-ink-side-dim group-hover:inline">
            {chord}
          </kbd>
        )}
      </NavLink>
    </li>
  );
}
